// Saved views (OEM-268): a filter saved under a name, listed under Projects in the sidebar, that runs
// over the whole board - every project, those with a board of their own too ("search the whole mailbox,
// not the inbox"). What a view holds is the board's own filters (the web's Filters, with Group by and the
// sort), as plain JSON. This module is the one place that says what a valid one is (the server checks
// every save with it), how an item matches one (the web's board, the sidebar's counts and Claude's
// list_items all use it), and the built-in quick views ("Waiting for me to verify", ...). Pure: the
// server, the web app and Claude's tools all use it.

import { isDueFilter, matchesDue, isOverdue, todayIn, type DueFilter } from "./due.ts";
import {
  assigneeNames,
  canVerify,
  draftHiddenFor,
  draftShows,
  isDraftsFilter,
  type DraftsFilter,
  flowLane,
  HIT_ROLES,
  isAssignee,
  LANES,
  reviewsWaitingFor,
  STAGE_LABEL,
  TAGS,
  teamsIn,
  type BoardItem,
  type ItemKind,
  type Lane,
  type Viewer,
} from "./shared.ts";

export type ViewSort = "num" | "priority" | "newest" | "attention" | "due";
export type ViewGroup = "none" | "project" | "person";

/** "@me" in a view's assignee list: whoever opens it (a shared "assigned to me" view works for everyone). */
export const ME = "@me";
/** nobody's items, in the assignee list (the web's NOBODY) */
export const NOBODY_ASG = "__none";

/** What a view holds: the board's filters, Group by and the sort. */
export interface ViewSpec {
  /** the queues (empty = every one, closed too) */
  lanes: Lane[];
  high: boolean;
  tag: string | null;
  role: string | null;
  /** member names, "__none" for nobody's, "@me" for whoever opens it */
  asg: string[];
  teams: "in" | "resolved" | "any" | null;
  q: string;
  notReviewed: boolean;
  nondev: boolean;
  notes: "all" | "people" | "ai";
  kind: Exclude<ItemKind, "project"> | "none" | null;
  /** one project's items (its # number); null = every project, and the items in none */
  project: number | null;
  /** only items in no project ("just the raw ones", OEM-260); never with `project` */
  noProject: boolean;
  /** leave these projects' items out (their # numbers, OEM-260): everything except project 03 */
  hideProjects: number[];
  /** drafts (OEM-286): others' hidden (yours show), everyone's shown too, or only drafts */
  drafts: DraftsFilter;
  due: DueFilter | null;
  sort: ViewSort;
  group: ViewGroup;
}

/** The board's opening filters: everything still in progress, every project. */
export const DEFAULT_SPEC: ViewSpec = {
  lanes: ["todo", "review", "deploy", "verify"],
  high: false,
  tag: null,
  role: null,
  asg: [],
  teams: null,
  q: "",
  notReviewed: false,
  nondev: false,
  notes: "all",
  kind: null,
  project: null,
  noProject: false,
  hideProjects: [],
  drafts: "hide",
  due: null,
  sort: "num",
  group: "none",
};

const SORTS: ViewSort[] = ["num", "priority", "newest", "attention", "due"];
const GROUPS: ViewGroup[] = ["none", "project", "person"];
const KINDS = ["feature", "bug", "task", "none"] as const;
const LANE_IDS = LANES.map((l) => l.id);
const TAG_IDS: string[] = TAGS.map((t) => t.id);
const ROLE_IDS: string[] = HIT_ROLES.map((r) => r.id);
const KEYS = Object.keys(DEFAULT_SPEC) as (keyof ViewSpec)[];

/** A view's name: 1 to 60 characters, no line breaks. */
export const MAX_VIEW_NAME = 60;

/** Why a name can't be a view's, or null. */
export function viewNameProblem(name: unknown): string | null {
  if (typeof name !== "string" || !name.trim()) return "Give the view a name.";
  if (name.trim().length > MAX_VIEW_NAME) return `Keep a view's name under ${MAX_VIEW_NAME} characters.`;
  if (/[\r\n]/.test(name)) return "A view's name is one line.";
  return null;
}

/**
 * A view's filters from anything (a request's JSON, a stored row): a ViewSpec, with what's left out
 * taken from the board's defaults, or what's wrong with it. Unknown keys, unknown values and wrong
 * types are refused, so what's stored is always something the board can open.
 */
export function parseViewSpec(input: unknown): { spec: ViewSpec } | { error: string } {
  if (!input || typeof input !== "object" || Array.isArray(input)) return { error: "filters must be an object." };
  const o = input as Record<string, unknown>;
  const extra = Object.keys(o).filter((k) => !(KEYS as string[]).includes(k));
  if (extra.length) return { error: `filters: unknown ${extra.length === 1 ? "key" : "keys"} ${extra.join(", ")}.` };
  const s: ViewSpec = { ...DEFAULT_SPEC, lanes: [...DEFAULT_SPEC.lanes], asg: [], hideProjects: [] };
  const bad = (k: string, what: string) => ({ error: `filters.${k} must be ${what}.` });
  const bool = (k: "high" | "notReviewed" | "nondev") => {
    if (o[k] === undefined) return null;
    if (typeof o[k] !== "boolean") return bad(k, "true or false");
    s[k] = o[k] as boolean;
    return null;
  };
  const oneOf = <K extends keyof ViewSpec>(k: K, allowed: readonly unknown[], what: string, nullable = true) => {
    if (o[k] === undefined) return null;
    if ((o[k] === null && nullable) || allowed.includes(o[k])) {
      (s as unknown as Record<string, unknown>)[k] = o[k];
      return null;
    }
    return bad(k, what);
  };
  if (o.lanes !== undefined) {
    if (!Array.isArray(o.lanes) || !o.lanes.every((l) => (LANE_IDS as unknown[]).includes(l))) return bad("lanes", `a list of ${LANE_IDS.join(", ")}`);
    s.lanes = LANE_IDS.filter((l) => (o.lanes as unknown[]).includes(l));
  }
  for (const k of ["high", "notReviewed", "nondev"] as const) {
    const e = bool(k);
    if (e) return e;
  }
  for (const e of [
    oneOf("tag", TAG_IDS, `one of ${TAG_IDS.join(", ")}, or null`),
    oneOf("role", ROLE_IDS, `one of ${ROLE_IDS.join(", ")}, or null`),
    oneOf("teams", ["in", "resolved", "any"], "in, resolved, any or null"),
    oneOf("notes", ["all", "people", "ai"], "all, people or ai", false),
    oneOf("kind", KINDS, "feature, bug, task, none or null"),
    oneOf("sort", SORTS, SORTS.join(", "), false),
    oneOf("group", GROUPS, GROUPS.join(", "), false),
  ])
    if (e) return e;
  if (o.due !== undefined) {
    if (o.due !== null && !isDueFilter(o.due)) return bad("due", "overdue, week, later, none or null");
    s.due = o.due as DueFilter | null;
  }
  if (o.asg !== undefined) {
    if (!Array.isArray(o.asg) || o.asg.length > 50 || !o.asg.every((a) => typeof a === "string" && a.trim().length > 0 && a.length <= 100)) return bad("asg", "a list of up to 50 names");
    s.asg = [...new Set((o.asg as string[]).map((a) => a.trim()))];
  }
  if (o.q !== undefined) {
    if (typeof o.q !== "string" || o.q.length > 200) return bad("q", "text of up to 200 characters");
    s.q = o.q.trim();
  }
  if (o.project !== undefined) {
    if (o.project !== null && !(Number.isInteger(o.project) && (o.project as number) > 0)) return bad("project", "a project's # number, or null");
    s.project = o.project as number | null;
  }
  // OEM-260: views saved before these exist keep opening as they were (the defaults)
  if (o.noProject !== undefined) {
    if (typeof o.noProject !== "boolean") return bad("noProject", "true or false");
    s.noProject = o.noProject;
  }
  if (o.hideProjects !== undefined) {
    if (!Array.isArray(o.hideProjects) || o.hideProjects.length > 100 || !o.hideProjects.every((n) => Number.isInteger(n) && (n as number) > 0)) return bad("hideProjects", "a list of projects' # numbers");
    s.hideProjects = [...new Set(o.hideProjects as number[])].sort((a, b) => a - b);
  }
  // OEM-286: views saved before drafts keep the default (others' drafts hidden)
  if (o.drafts !== undefined) {
    if (!isDraftsFilter(o.drafts)) return bad("drafts", "hide, show or only");
    s.drafts = o.drafts;
  }
  if (s.noProject && s.project != null) return { error: "filters: noProject and project can't both be set (no project means items in none)." };
  return { spec: s };
}

/** A stored view's filters, or the defaults when a stored row somehow isn't one any more. */
export const specOrDefault = (raw: unknown): ViewSpec => {
  const r = parseViewSpec(raw);
  return "spec" in r ? r.spec : { ...DEFAULT_SPEC };
};

/** Two specs say the same (Save changes shows while they don't). */
export function sameSpec(a: ViewSpec, b: ViewSpec): boolean {
  const norm = (s: ViewSpec) => JSON.stringify({ ...s, lanes: LANE_IDS.filter((l) => s.lanes.includes(l)), asg: [...s.asg].sort(), hideProjects: [...(s.hideProjects ?? [])].sort((x, y) => x - y), noProject: !!s.noProject });
  return norm(a) === norm(b);
}

/** Written by someone's AI app (a note or a reaction). */
const hasAi = (it: BoardItem) => it.notes.some((n) => !!n.agent) || it.reactions.some((r) => !!r.agent);

/** What matching needs besides the spec: who's looking (for "@me") and today on the board (for Due). */
export interface MatchContext {
  /** the viewer's name: "@me" in the assignee list means them */
  me?: string | null;
  /** the viewer's member id: their own drafts show (OEM-286); without it, every draft is someone else's */
  meId?: string | null;
  /** today on the board ("2026-10-15"); default: today in the board's default time zone */
  today?: string;
  /** non-dev items too, though the Non-dev filter is off (the board's own Non-dev section) */
  allowNonDev?: boolean;
}

/**
 * The board's filters, as the web's toolbar has always applied them: the queues (a fix with a PR ready
 * for review is in PR review), High, Who hits it, the assignee list, Where, Teams, Not yet reviewed,
 * Notes (AI only), Kind, Due, the search, and No project and Hide projects (OEM-260). Which one project is the
 * caller's (inViewScope). Pure.
 */
export function matchesFilters(
  it: BoardItem,
  f: Omit<ViewSpec, "lanes" | "sort" | "group" | "project" | "noProject" | "hideProjects" | "drafts"> &
    Partial<Pick<ViewSpec, "noProject" | "hideProjects" | "drafts">> & { lanes: ReadonlySet<Lane> | readonly Lane[] },
  ctx: MatchContext = {},
): boolean {
  // drafts (OEM-286): someone else's draft only with Show drafts (or Only drafts); your own always
  if (!draftShows(it, f.drafts ?? "hide", ctx.meId)) return false;
  // the Project filter's No project and Hide (OEM-260): a project counts as its own (a project's card
  // is in no project, so No project leaves projects themselves out too)
  if (f.noProject && (it.project || it.kind === "project")) return false;
  if (f.hideProjects?.length) {
    const p = it.kind === "project" ? it.num : it.project?.num;
    if (p != null && f.hideProjects.includes(p)) return false;
  }
  // non-dev items sit in their own section unless the Non-dev chip is on
  if (f.nondev) {
    if (!it.nondev) return false;
  } else if (!ctx.allowNonDev && it.nondev) return false;
  const lanes: ReadonlySet<Lane> = f.lanes instanceof Set ? f.lanes : new Set(f.lanes as readonly Lane[]);
  if (lanes.size && !lanes.has(flowLane(it))) return false;
  if (f.high && it.priority !== "high") return false;
  if (f.role && it.role !== f.role) return false;
  if (f.asg.length) {
    // any of the people it is with (OEM-285); NOBODY_ASG: nobody
    const people = assigneeNames(it);
    const who = people.length ? people : [NOBODY_ASG];
    if (!f.asg.some((a) => (a === ME ? !!ctx.me && who.includes(ctx.me) : who.includes(a)))) return false;
  }
  if (f.tag && !it.tags.includes(f.tag)) return false;
  if (f.teams) {
    const ok = f.teams === "in" ? teamsIn(it) : f.teams === "resolved" ? it.teamsResolved : teamsIn(it) || it.teamsResolved;
    if (!ok) return false;
  }
  if (f.notReviewed && it.reviewed) return false;
  if (f.notes === "ai" && !hasAi(it)) return false;
  if (f.kind && (f.kind === "none" ? it.kind !== null : it.kind !== f.kind)) return false;
  if (f.due && !matchesDue(it, f.due, ctx.today ?? todayIn())) return false;
  if (f.q) {
    const hay = (
      `${it.num} ${it.key?.label ?? ""} ${it.formerKeys.join(" ")} ${(it.movedFrom ?? []).map((m) => `${m.board.replace(/ board$/, "")} #${m.num} ${m.key ?? ""}`).join(" ")} ${it.title} ${it.body} ${it.tags.join(" ")} ${it.flag ?? ""} ${it.role ?? ""}` +
      (teamsIn(it) ? " teams added-via-teams" : "") +
      (it.teamsResolved ? " teams resolved-via-teams" : "") +
      ` ${STAGE_LABEL[it.stage]}${it.developed?.note ? ` ${it.developed.note}` : ""}${it.deployed?.note ? ` ${it.deployed.note}` : ""}`
    ).toLowerCase();
    for (const term of f.q.toLowerCase().split(/\s+/).filter(Boolean)) if (!hay.includes(term)) return false;
  }
  return true;
}

/**
 * What a view runs over: the whole board - every project, those with a board of their own too, and the
 * items in none - or one project's items when its Project filter says so. Projects themselves aren't
 * work items, so they're left out (as on the main board). Archived items aren't on the board.
 */
export const inViewScope = (it: Pick<BoardItem, "num" | "kind" | "project" | "archived">, spec: Pick<ViewSpec, "project">) =>
  it.kind !== "project" && !it.archived && (spec.project == null || it.project?.num === spec.project);

/** The items a saved view shows (in board order; the caller sorts). */
export function viewItems<I extends BoardItem>(items: I[], spec: ViewSpec, ctx: MatchContext = {}): I[] {
  return items.filter((it) => inViewScope(it, spec) && matchesFilters(it, spec, ctx));
}

/** A view's count in the sidebar: the open items it matches. */
export const viewCount = (items: BoardItem[], spec: ViewSpec, ctx: MatchContext = {}) => viewItems(items, spec, ctx).filter((it) => it.status === "open").length;

// -- Quick views: built in, worked out for whoever looks, never stored --------------------------

export type QuickViewId = "verify" | "todo" | "review" | "deploy" | "overdue";
export const QUICK_VIEWS: { id: QuickViewId; name: string; hint: string; company?: boolean }[] = [
  { id: "verify", name: "Waiting for me to verify", hint: "Deployed fixes in every project that you may verify (never one you developed yourself)" },
  { id: "todo", name: "My to-do", hint: "Assigned to you or claimed by you, still To do, in every project" },
  { id: "review", name: "PRs for my review", hint: "In PR review and waiting for you as a reviewer (OEMUAP, GitHub)", company: true },
  { id: "deploy", name: "Deploy queue", hint: "Developed, waiting to be deployed, in every project" },
  { id: "overdue", name: "Overdue", hint: "Open and past their due date, in every project" },
];
export const QUICK_PREFIX = "quick:";
export const isQuickViewId = (v: unknown): v is QuickViewId => typeof v === "string" && QUICK_VIEWS.some((q) => q.id === v);
/** "quick:verify" -> "verify"; anything else -> null. */
export const quickOf = (id: string | null | undefined): QuickViewId | null => {
  const q = id?.startsWith(QUICK_PREFIX) ? id.slice(QUICK_PREFIX.length) : null;
  return isQuickViewId(q) ? q : null;
};
/** The quick views a board offers: PRs for my review is OEMUAP's (a personal board has no GitHub). */
export const quickViewsFor = (company: boolean) => QUICK_VIEWS.filter((q) => company || !q.company);

/**
 * The spec a quick view opens with, Group by project: as much of its rule as filters can say (its own
 * rule still picks the items), so "Save as new view" from one keeps its sense: My to-do is To do and
 * assigned to "@me"; Overdue is Due: overdue, soonest first; the others are their queue.
 */
export function quickSpec(id: QuickViewId): ViewSpec {
  const base: ViewSpec = { ...DEFAULT_SPEC, asg: [], group: "project" };
  switch (id) {
    case "verify":
      return { ...base, lanes: ["verify"] };
    case "todo":
      return { ...base, lanes: ["todo"], asg: [ME] };
    case "review":
      return { ...base, lanes: ["review"] };
    case "deploy":
      return { ...base, lanes: ["deploy"] };
    case "overdue":
      return { ...base, lanes: [], due: "overdue", sort: "due" };
  }
}

/** Whether an item is in a quick view, for this viewer. Every project, never projects or archived items. */
export function quickMatches(
  id: QuickViewId,
  it: BoardItem,
  viewer: Pick<Viewer, "id" | "role" | "isAdmin"> & { canBuild?: boolean; isVerifier?: boolean; org?: Viewer["org"] },
  today: string,
  reviews?: Set<number>,
): boolean {
  if (it.kind === "project" || it.archived) return false;
  // someone else's draft (OEM-286) is in nobody else's quick views
  if (draftHiddenFor(it, viewer.id)) return false;
  switch (id) {
    case "verify":
      return flowLane(it) === "verify" && canVerify(viewer, it);
    case "todo":
      return it.status === "open" && isAssignee(it, viewer.id) && flowLane(it) === "todo";
    case "review":
      return !!reviews?.has(it.num);
    case "deploy":
      return flowLane(it) === "deploy";
    case "overdue":
      return isOverdue(it, today);
  }
}

/** The items a quick view shows for this viewer. */
export function quickItems<I extends BoardItem>(id: QuickViewId, items: I[], viewer: Viewer, today: string): I[] {
  const reviews = id === "review" ? new Set(reviewsWaitingFor(items, viewer.id).map((r) => r.item.num)) : undefined;
  return items.filter((it) => quickMatches(id, it, viewer, today, reviews));
}

// -- The URL: ?view=<id> or ?view=quick:verify ------------------------------------------------

/** ?view= values that are pages, not saved views. */
const PAGES = new Set(["whatsnew", "people", "settings"]);

/** The view a URL opens (?view=<id> or ?view=quick:verify), or null (none, or one of the pages). */
export function readViewParam(search: string): string | null {
  const v = new URLSearchParams(search).get("view")?.trim();
  if (!v || PAGES.has(v)) return null;
  return /^[\w:-]{1,64}$/.test(v) ? v : null;
}

/** The query string with a view in it (or taken out: null), "" or "?...". A page's ?view= is left alone. */
export function writeViewParam(search: string, id: string | null): string {
  const q = new URLSearchParams(search);
  const cur = q.get("view");
  if (!cur || !PAGES.has(cur)) {
    q.delete("view");
    if (id) q.set("view", id);
  }
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

/** A saved view, as the sidebar and Claude list it. */
export interface SavedViewView {
  id: string;
  name: string;
  spec: ViewSpec;
  /** shared with the board (no owner) */
  shared: boolean;
  /** the viewer's own */
  mine: boolean;
  /** the viewer may rename it, change its filters, reorder and delete it */
  canEdit: boolean;
  /** who made it */
  by: string | null;
  position: number;
  updatedAt: string;
}
